import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { type SchemaListFilters } from '../../../types/schema';
import { LuPlus } from 'react-icons/lu';
import { LoaderState, InlineErrorState } from '../../common';
import { SchemaListFiltersBar } from '../schema-list-filters';
import { SchemaListTable } from '../schema-list-table';
import { StyledSchemaListTitle } from './styles';
import { useSchemaList } from './use-schema-list';

type SchemaListProps = {
  filters: SchemaListFilters;
  onFiltersChange: (filters: SchemaListFilters) => void;
};

export const SchemaList = ({ filters, onFiltersChange }: SchemaListProps) => {
  const {
    columns,
    data,
    error,
    handleArchiveCancel,
    handleArchiveConfirm,
    isError,
    isLoading,
    schemaRegistry,
    schemaToArchive,
    setSorting,
    sorting,
  } = useSchemaList({ filters });

  return (
    <>
      <Modal
        onClose={handleArchiveCancel}
        opened={schemaToArchive !== null}
        title="Archive schema"
      >
        <Text size="sm">
          Archive <strong>{schemaToArchive?.name}</strong>? This cannot be
          undone.
        </Text>
        <Group position="right" mt="md" spacing="xs">
          <Button onClick={handleArchiveCancel} variant="default">
            Cancel
          </Button>
          <Button color="red" onClick={() => void handleArchiveConfirm()}>
            Archive
          </Button>
        </Group>
      </Modal>

      <Stack spacing="xl">
        <Box>
          <Group position="apart" align="center" mb="md">
            <Box>
              <StyledSchemaListTitle>Schemas</StyledSchemaListTitle>
            </Box>
            <ActionIcon
              color="cyan"
              size="lg"
              variant="filled"
              component={Link}
              to="/schemas/new"
            >
              <LuPlus size={16} />
            </ActionIcon>
          </Group>
        </Box>

        <SchemaListFiltersBar
          filters={filters}
          onFiltersChange={onFiltersChange}
          registry={schemaRegistry}
        />

        <Box>
          {isLoading && <LoaderState label="Loading schemas..." />}

          {isError && (
            <InlineErrorState
              message={
                error instanceof Error
                  ? error.message
                  : 'Failed to load schemas.'
              }
            />
          )}

          {!isLoading && !isError && (
            <SchemaListTable
              columns={columns}
              data={data}
              onSortingChange={setSorting}
              sorting={sorting}
            />
          )}
        </Box>
      </Stack>
    </>
  );
};
